import { Link } from "../lib/router";
import { MrrChart } from "../components/LazyCharts";
import { EnvVatPeriodControls } from "../components/Controls";
import { PageHeader } from "../components/Shell";
import { AppIcon, BarList, Card, CommandCenterButton, Dot, EmptyState, ErrorState, KpiCard, ProviderSplitCard, Sparkline, TableWrap } from "../components/ui";
import { ConcentrationCard, ContributionCard, GrowthCard, RpiKpi } from "../components/insights";
import { OverviewSkeleton, OverviewMobileSkeleton } from "../components/skeletons";
import { getOverview } from "../lib/api-admin";
import { ratingShort } from "../lib/insights";
import { PERIOD_LONG, VAT_SUBTITLES, eur, eventTypeMeta, int, linkStatusMeta, relativeNl } from "../lib/format";
import { usePrefs } from "../lib/prefs";
import { useAsync } from "../lib/useAsync";
import { useIsMobile } from "../lib/useMobile";
import { OverviewMobileBody, OverviewMobileTop } from "./OverviewMobile";

export function Overview() {
  const { env, period, vat } = usePrefs();
  const s = useAsync(() => getOverview({ period, environment: env, vat }), [period, env, vat]);
  const d = s.data;
  const mobile = useIsMobile();

  const subtitle = d
    ? `${d.apps.length} ${d.apps.length === 1 ? "app" : "apps"} uit App Store Connect · ${PERIOD_LONG[period]} · ${VAT_SUBTITLES[vat]}${env === "sandbox" ? " · sandbox" : ""}`
    : "Omzet en gebruik van al je apps";

  return (
    <>
      {mobile ? (
        <OverviewMobileTop />
      ) : (
        <PageHeader title="Overzicht" subtitle={subtitle}>
          <EnvVatPeriodControls />
        </PageHeader>
      )}

      {env === "sandbox" && <div className="banner">Sandbox-data: testaankopen. Trendgrafieken zijn er alleen voor productie.</div>}
      {s.status === "loading" && (mobile ? <OverviewMobileSkeleton /> : <OverviewSkeleton />)}
      {s.status === "error" && <ErrorState message={s.error} onRetry={s.reload} />}

      {s.status === "ready" && d && mobile && (
        <div>
          {d.fx.missing > 0 && (
            <div className="banner banner-warn">
              {d.fx.missing} {d.fx.missing === 1 ? "transactie" : "transacties"} in {d.fx.currencies.join(", ")} zonder wisselkoers {d.fx.missing === 1 ? "is" : "zijn"} niet meegeteld.
            </div>
          )}
          <OverviewMobileBody d={d} />
        </div>
      )}

      {s.status === "ready" && d && !mobile && (
        <div>
          <div className="kpi-grid">
            <KpiCard label="MRR" value={eur(d.kpis.mrr.value)} previous={{ current: d.kpis.mrr.value, before: d.kpis.mrr.previous }} />
            <KpiCard label="Actieve abonnementen" value={int(d.kpis.active_subscriptions.value)} previous={{ current: d.kpis.active_subscriptions.value, before: d.kpis.active_subscriptions.previous }} />
            <KpiCard label="Actieve trials" value={int(d.kpis.active_trials.value)} previous={{ current: d.kpis.active_trials.value, before: d.kpis.active_trials.previous }} />
            <KpiCard label="Omzet (periode)" value={eur(d.kpis.revenue.value)} previous={{ current: d.kpis.revenue.value, before: d.kpis.revenue.previous }} />
            <KpiCard label="Op telefoon" value={int(d.kpis.active_on_phone.value)} previous={{ current: d.kpis.active_on_phone.value, before: d.kpis.active_on_phone.previous }} hint="toestellen, 7 dagen" />
            <RpiKpi rpi={d.rpi} />
          </div>

          {d.fx.missing > 0 && (
            <div className="banner banner-warn">
              {d.fx.missing} {d.fx.missing === 1 ? "transactie" : "transacties"} in {d.fx.currencies.join(", ")} zonder wisselkoers zijn niet meegeteld.
            </div>
          )}

          <Card title="MRR">
            {d.mrr_series.length >= 2 ? (
              <MrrChart points={d.mrr_series} />
            ) : (
              <EmptyState title={env === "sandbox" ? "Geen trend voor sandbox" : "Nog geen trenddata"}>
                {env === "sandbox"
                  ? "Snapshots worden alleen voor productie bewaard."
                  : "De dagelijkse snapshot (03:00 UTC) bouwt de lijn op; vanaf de tweede dag verschijnt de grafiek."}
              </EmptyState>
            )}
          </Card>

          <div className="grid-2">
            <GrowthCard deltas={d.deltas} />
            <ContributionCard contribution={d.contribution} movers={d.movers} />
          </div>

          <ConcentrationCard c={d.concentration} />

          <div className="grid-2">
            <Card title="MRR per app">
              {d.mrr_by_app.length === 0 || d.mrr_by_app.every((r) => r.value === 0) ? (
                <EmptyState title="Nog geen MRR">Zodra er een actief abonnement is, verschijnt het hier.</EmptyState>
              ) : (
                <BarList rows={d.mrr_by_app.map((r) => ({ key: r.app_id, label: r.name, value: r.value }))} />
              )}
            </Card>

            <Card title="Live gebeurtenissen">
              {d.events.length === 0 ? (
                <EmptyState title="Nog geen gebeurtenissen">Aankopen, trials, refunds en koppelingen verschijnen hier zodra ze binnenkomen.</EmptyState>
              ) : (
                <ul className="events">
                  {d.events.map((e, i) => {
                    const m = eventTypeMeta(e.type);
                    return (
                      <li key={`${e.at}-${i}`}>
                        <Dot tone={m.tone} title={m.label} />
                        <span className="events-label">{e.label}</span>
                        <span className="muted events-time">{relativeNl(e.at)}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          </div>

          <ProviderSplitCard split={d.revenue_by_provider} />

          <Card title="Apps">
            {d.apps.length === 0 ? (
              <EmptyState title="Nog geen apps">Apps uit App Store Connect worden automatisch gekoppeld; er hoeft niets handmatig te worden aangemaakt.</EmptyState>
            ) : (
              <TableWrap>
                <table>
                  <thead>
                    <tr>
                      <th>App</th>
                      <th>Bundle-ID</th>
                      <th>Koppeling</th>
                      <th className="num">MRR</th>
                      <th className="num">Omzet</th>
                      <th className="num">Abonnees</th>
                      <th className="num">Trials</th>
                      <th className="num">Op telefoon</th>
                      <th>Beoordeling</th>
                      <th>Trend</th>
                      <th>Command center</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.apps.map((a) => {
                      const link = linkStatusMeta(a.link_status);
                      return (
                        <tr key={a.id}>
                          <td>
                            <Link to={`/apps/${encodeURIComponent(a.id)}`} className="app-cell">
                              <AppIcon name={a.name} url={a.icon_url} size={30} seed={a.id} />
                              <strong>{a.name}</strong>
                            </Link>
                          </td>
                          <td className="muted mono">{a.bundle_id ?? "—"}</td>
                          <td>
                            <span className="status-cell">
                              <Dot tone={link.tone} title={link.label} />
                              <span className="muted">{link.label}</span>
                            </span>
                          </td>
                          <td className="num">{eur(a.mrr)}</td>
                          <td className="num">{eur(a.revenue)}</td>
                          <td className="num">{int(a.subscribers)}</td>
                          <td className="num">{int(a.trials)}</td>
                          <td className="num">{int(a.active_on_phone.value)}</td>
                          <td className="muted">{ratingShort(a.rating)}</td>
                          <td>
                            <Sparkline values={a.sparkline.map((p) => p.value)} />
                          </td>
                          <td>
                            <CommandCenterButton url={a.command_center_url} appId={a.id} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </TableWrap>
            )}
          </Card>
        </div>
      )}
    </>
  );
}
